import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD } from './designTokens';

interface VisualTableLayoutProps {
  onSelectTable: (tableId: string) => void;
  refreshKey: number;
}

interface SeatInfo {
  id: string;
  seatNumber: number;
  status: string;
  guestName: string | null;
  ticketNumber: string | null;
  orderReferenceId: string | null;
  familyAssociation: string | null;
  chapterAffiliation: string | null;
  protocolVip: boolean;
}

interface TableInfo {
  id: string;
  tableNumber: number;
  tableName: string;
  capacity: number;
  zoneName: string;
  zoneId: string;
  zoneColor: string;
  seats: SeatInfo[];
  isEmpty: boolean;
}

interface UnseatedGuestInfo {
  fullName: string;
  ticketNumber: string | null;
  familyAssociation: string | null;
  orderReferenceId: string | null;
}

interface OrderGuest {
  id: string;
  fullName: string;
  familyAssociation: string | null;
  ticketNumber: string | null;
}

interface OrderGroup {
  orderId: string;
  referenceId: string;
  guestCount: number;
  primaryChapter: string | null;
  tier: string | null;
  guests: OrderGuest[];
}

interface ConfirmDialogState {
  order: OrderGroup;
  table: TableInfo;
  warnings: string[];
}

interface ZoneGroup {
  name: string;
  id: string;
  color: string;
  tables: TableInfo[];
  totalSeats: number;
  occupiedSeats: number;
  availableSeats: number;
  unseatedChapterGuests: number;
  unseatedGuests: UnseatedGuestInfo[];
}

const SEAT_COLORS: Record<string, string> = {
  Available: '#4caf50',
  Assigned: '#1565c0',
  CheckedIn: '#7b1fa2',
  Held: '#f9a825',
  Blocked: '#757575',
};

export function VisualTableLayout({ onSelectTable, refreshKey }: VisualTableLayoutProps) {
  const [zones, setZones] = useState<ZoneGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [seatFilter, setSeatFilter] = useState<'all' | 'has-available' | 'full'>('all');
  const [selectedZones, setSelectedZones] = useState<Set<string>>(new Set());
  const [dragTableId, setDragTableId] = useState<string | null>(null);
  const [dropTargetZone, setDropTargetZone] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  // Order pill drag-and-drop state
  const [unseatedOrders, setUnseatedOrders] = useState<OrderGroup[]>([]);
  const [dragOrder, setDragOrder] = useState<OrderGroup | null>(null);
  const [dropTargetTableId, setDropTargetTableId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

  const handleDrop = useCallback(async (tableId: string, targetZoneId: string) => {
    try {
      await apiClient(`/table-management/tables/${tableId}`, {
        method: 'PATCH',
        body: { zoneId: targetZoneId },
      });
      setLocalRefresh((n) => n + 1);
    } catch {
      // ignore
    }
  }, []);

  // Check if an order is locked (family/RES group)
  const isOrderLocked = useCallback((order: OrderGroup): boolean => {
    return order.guests.some((g) => {
      if (!g.familyAssociation) return false;
      const fa = g.familyAssociation.toUpperCase();
      return fa.includes('RES') || fa.includes('FAMILY');
    });
  }, []);

  // Get orders for a given zone (match by chapter → zone name)
  const getOrdersForZone = useCallback((zoneName: string): OrderGroup[] => {
    return unseatedOrders.filter((order) => {
      if (!order.primaryChapter) return false;
      const chapter = order.primaryChapter.toLowerCase();
      const zone = zoneName.toLowerCase();
      return zone.includes(chapter) || chapter.includes(zone);
    });
  }, [unseatedOrders]);

  // Handle order pill drop on a table
  const handleOrderDrop = useCallback((order: OrderGroup, table: TableInfo) => {
    const availableSeats = table.seats.filter((s) => s.status === 'Available').length;
    const warnings: string[] = [];

    if (availableSeats < order.guestCount) {
      warnings.push(`Not enough seats: ${availableSeats} available, ${order.guestCount} needed`);
    }

    // Tier mismatch check (non-patron at VIP zone)
    const zoneNameLower = table.zoneName.toLowerCase();
    const orderTier = (order.tier || '').toLowerCase();
    if (zoneNameLower.includes('vip') || zoneNameLower.includes('patron')) {
      if (orderTier !== 'patron' && orderTier !== 'vip') {
        warnings.push(`Tier mismatch: ${order.tier || 'General'} order going to ${table.zoneName} zone`);
      }
    }

    if (order.guestCount > table.capacity) {
      warnings.push(`Capacity overflow: order has ${order.guestCount} guests but table capacity is ${table.capacity}`);
    }

    setConfirmDialog({ order, table, warnings });
  }, []);

  // Confirm seating assignment
  const handleConfirmSeat = useCallback(async () => {
    if (!confirmDialog) return;
    const { order, table } = confirmDialog;
    try {
      const guestIds = order.guests.map((g) => g.id);
      await apiClient('/seating-intelligence/accept-suggestion', {
        method: 'POST',
        body: { guestIds, tableId: table.id },
      });
      setLocalRefresh((n) => n + 1);
    } catch {
      // ignore errors for now
    } finally {
      setConfirmDialog(null);
      setDragOrder(null);
      setDropTargetTableId(null);
    }
  }, [confirmDialog]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tables, unassigned, unseatedQueue] = await Promise.all([
        apiClient<Array<{
          id: string;
          tableNumber: number;
          tableName: string;
          capacity: number;
          zone: { id: string; name: string; colorCode: string };
          seats: Array<{ id: string; seatNumber: number; status: string; guest?: { fullName: string; ticketNumber?: string | null; orderReferenceId?: string | null; familyAssociation?: string | null; chapterAffiliation?: string | null; protocolVip?: boolean } | null }>;
        }>>('/table-management/tables?include=seats'),
        apiClient<Array<{ fullName: string; chapterAffiliation: string | null; ticketNumber?: string | null; familyAssociation?: string | null; referenceId?: string | null }>>('/table-management/reports/unassigned'),
        apiClient<{ orders: OrderGroup[]; singles: unknown[] }>('/seating-intelligence/unseated-queue').catch(() => ({ orders: [] as OrderGroup[], singles: [] })),
      ]);

      setUnseatedOrders(unseatedQueue.orders || []);

      // Group unseated guests by chapter (case-insensitive)
      const unseatedByChapter = new Map<string, { count: number; guests: UnseatedGuestInfo[] }>();
      for (const g of unassigned) {
        if (g.chapterAffiliation) {
          const key = g.chapterAffiliation.toLowerCase();
          if (!unseatedByChapter.has(key)) unseatedByChapter.set(key, { count: 0, guests: [] });
          const entry = unseatedByChapter.get(key)!;
          entry.count++;
          entry.guests.push({
            fullName: g.fullName,
            ticketNumber: g.ticketNumber || null,
            familyAssociation: g.familyAssociation || null,
            orderReferenceId: g.referenceId || null,
          });
        }
      }

      // Group by zone
      const zoneMap = new Map<string, { id: string; color: string; tables: TableInfo[] }>();
      for (const t of tables) {
        const zoneName = t.zone.name;
        if (!zoneMap.has(zoneName)) zoneMap.set(zoneName, { id: t.zone.id, color: t.zone.colorCode, tables: [] });
        const occupiedCount = (t.seats || []).filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn').length;
        zoneMap.get(zoneName)!.tables.push({
          id: t.id,
          tableNumber: t.tableNumber,
          tableName: t.tableName,
          capacity: t.capacity,
          zoneName,
          zoneId: t.zone.id,
          zoneColor: t.zone.colorCode,
          isEmpty: occupiedCount === 0,
          seats: (t.seats || []).map((s) => ({
            id: s.id,
            seatNumber: s.seatNumber,
            status: s.status,
            guestName: s.guest?.fullName || null,
            ticketNumber: s.guest?.ticketNumber || null,
            orderReferenceId: s.guest?.orderReferenceId || null,
            familyAssociation: s.guest?.familyAssociation || null,
            chapterAffiliation: s.guest?.chapterAffiliation || null,
            protocolVip: s.guest?.protocolVip || false,
          })),
        });
      }

      const zoneGroups: ZoneGroup[] = Array.from(zoneMap.entries())
        .map(([name, data]) => {
          const totalSeats = data.tables.reduce((s, t) => s + t.seats.length, 0);
          const occupiedSeats = data.tables.reduce(
            (s, t) => s + t.seats.filter((seat) => seat.status === 'Assigned' || seat.status === 'CheckedIn').length, 0,
          );
          // Match zone name to chapter name for unseated count (fuzzy: zone contains chapter or vice versa)
          let unseatedChapterGuests = 0;
          const unseatedGuests: UnseatedGuestInfo[] = [];
          for (const [chapter, chData] of unseatedByChapter) {
            const zoneLower = name.toLowerCase();
            if (zoneLower.includes(chapter) || chapter.includes(zoneLower)) {
              unseatedChapterGuests += chData.count;
              unseatedGuests.push(...chData.guests);
            }
          }
          const availableSeats = totalSeats - occupiedSeats;
          return { name, id: data.id, color: data.color, tables: data.tables.sort((a, b) => a.tableNumber - b.tableNumber), totalSeats, occupiedSeats, availableSeats, unseatedChapterGuests, unseatedGuests };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setZones(zoneGroups);
    } catch {
      setError('Failed to load table data.');
    } finally {
      setLoading(false);
    }
  }, [refreshKey, localRefresh]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>Loading visual layout...</div>;
  if (error) return <div style={{ padding: '1rem', color: '#d32f2f', backgroundColor: '#fdecea', borderRadius: '6px' }}>{error}</div>;

  return (
    <div>
      {/* Filter + Legend row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
        {/* Seat filter pills */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {([['all', 'All Tables'], ['has-available', 'Seats Available'], ['full', 'Full Tables']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSeatFilter(key)}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: seatFilter === key ? `2px solid ${PRIMARY}` : `1px solid ${BORDER_LIGHT}`,
                background: seatFilter === key ? '#e8f5e9' : BG_CARD,
                color: seatFilter === key ? PRIMARY : TEXT_MUTED,
                fontSize: '0.78rem',
                fontWeight: seatFilter === key ? 700 : 500,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(SEAT_COLORS).map(([status, color]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: TEXT_MUTED }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color }} />
              {status}
            </div>
          ))}
        </div>
      </div>

      {/* Zone filter pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px', padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: `1px solid ${BORDER_LIGHT}` }}>
        <span style={{ fontSize: '0.72rem', color: TEXT_MUTED, fontWeight: 600, alignSelf: 'center', marginRight: '4px' }}>Zones:</span>
        {zones.map((zone) => {
          const isSelected = selectedZones.size === 0 || selectedZones.has(zone.name);
          return (
            <button
              key={zone.name}
              onClick={() => {
                setSelectedZones((prev) => {
                  const next = new Set(prev);
                  if (prev.size === 0) {
                    // First click: select only this zone
                    next.add(zone.name);
                  } else if (next.has(zone.name)) {
                    next.delete(zone.name);
                    // If all removed, show all
                  } else {
                    next.add(zone.name);
                  }
                  return next;
                });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 12px',
                borderRadius: '16px',
                border: isSelected ? `2px solid ${zone.color}` : `1px solid #d1d5db`,
                background: isSelected ? `${zone.color}18` : '#fff',
                color: isSelected ? TEXT_DARK : TEXT_MUTED,
                fontSize: '0.75rem',
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: zone.color }} />
              {zone.name}
              <span style={{ fontSize: '0.65rem', color: TEXT_MUTED }}>({zone.tables.length})</span>
            </button>
          );
        })}
        {selectedZones.size > 0 && (
          <button
            onClick={() => setSelectedZones(new Set())}
            style={{ padding: '4px 10px', borderRadius: '16px', border: 'none', background: '#fdecea', color: '#d32f2f', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* All zones stacked */}
      {zones.map((zone) => {
        // Zone filter: skip if not selected
        if (selectedZones.size > 0 && !selectedZones.has(zone.name)) return null;
        const pct = zone.totalSeats > 0 ? Math.round((zone.occupiedSeats / zone.totalSeats) * 100) : 0;
        const filteredTables = zone.tables.filter((table) => {
          if (seatFilter === 'all') return true;
          const avail = table.seats.filter((s) => s.status === 'Available').length;
          if (seatFilter === 'has-available') return avail > 0;
          return avail === 0; // 'full'
        });
        if (seatFilter !== 'all' && filteredTables.length === 0 && zone.tables.length === 0) return null;
        return (
          <div key={zone.name} style={{ marginBottom: '2rem' }}
            onDragOver={(e) => { e.preventDefault(); setDropTargetZone(zone.id); }}
            onDragLeave={() => setDropTargetZone(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDropTargetZone(null);
              if (dragTableId) {
                handleDrop(dragTableId, zone.id);
                setDragTableId(null);
              }
            }}
          >
            {/* Zone header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingBottom: '8px', borderBottom: `2px solid ${dropTargetZone === zone.id ? '#1a6b3c' : zone.color}`, backgroundColor: dropTargetZone === zone.id ? '#e8f5e9' : '#fff', borderRadius: dropTargetZone === zone.id ? '6px 6px 0 0' : undefined, padding: dropTargetZone === zone.id ? '8px 10px' : '8px 10px', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
              <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: zone.color, flexShrink: 0 }} />
              <span style={{ fontSize: '1rem', fontWeight: 700, color: TEXT_DARK }}>{zone.name}</span>
              <span style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>{zone.tables.length} tables</span>
              <span style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>&bull;</span>
              <span style={{ fontSize: '0.78rem', color: '#2e7d32', fontWeight: 600 }}>{zone.occupiedSeats}/{zone.totalSeats} seats ({pct}%)</span>
              <span style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>&bull;</span>
              <span style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600 }}>{zone.availableSeats} avail</span>
              {zone.unseatedChapterGuests > 0 && (
                <>
                  <span style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>&bull;</span>
                  <UnseatedPopover count={zone.unseatedChapterGuests} guests={zone.unseatedGuests} />
                </>
              )}
              {(() => {
                const deficit = zone.unseatedChapterGuests - zone.availableSeats;
                if (deficit >= 0) {
                  const tablesNeeded = Math.floor(deficit / 10) + 1;
                  return (
                    <>
                      <span style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>&bull;</span>
                      <span style={{ fontSize: '0.72rem', color: '#d32f2f', fontWeight: 600, fontStyle: 'italic' }}>
                        Consider adding {tablesNeeded} table{tablesNeeded > 1 ? 's' : ''}
                      </span>
                    </>
                  );
                }
                return null;
              })()}
              {seatFilter !== 'all' && <span style={{ fontSize: '0.72rem', color: PRIMARY, fontWeight: 600 }}>({filteredTables.length} match)</span>}
            </div>

            {/* Order Pills Row */}
            {(() => {
              const zoneOrders = getOrdersForZone(zone.name);
              if (zoneOrders.length === 0) return null;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px', padding: '8px 10px', backgroundColor: '#fafbfc', borderRadius: '6px', border: `1px solid ${BORDER_LIGHT}` }}>
                  <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, fontWeight: 600, alignSelf: 'center', marginRight: '4px' }}>Orders:</span>
                  {zoneOrders.map((order) => {
                    const locked = isOrderLocked(order);
                    const tierColor = (order.tier || '').toLowerCase() === 'patron' ? '#c5a000' : '#2e7d32';
                    const pillBg = locked ? '#e0e0e0' : (order.tier || '').toLowerCase() === 'patron' ? '#fff8e1' : '#e8f5e9';
                    return (
                      <OrderPillWithPopover key={order.orderId} order={order} locked={locked} tierColor={tierColor} pillBg={pillBg} setDragOrder={setDragOrder} setDropTargetTableId={setDropTargetTableId} />
                    );
                  })}
                </div>
              );
            })()}

            {/* Table cards grid: 5 per row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px' }}>
              {zone.tables.map((table) => {
                const matches = filteredTables.includes(table);
                if (seatFilter === 'all' || matches) {
                  return <CircularTableCard key={table.id} table={table} onClick={() => onSelectTable(table.id)} onDragStart={() => setDragTableId(table.id)} dragOrder={dragOrder} dropTargetTableId={dropTargetTableId} onOrderDragOver={(tableId) => setDropTargetTableId(tableId)} onOrderDragLeave={() => setDropTargetTableId(null)} onOrderDrop={(order, tbl) => handleOrderDrop(order, tbl)} />;
                }
                // Non-matching table: show faded outline placeholder
                return (
                  <div
                    key={table.id}
                    style={{
                      borderRadius: '12px',
                      padding: '12px',
                      border: `2px dashed #bbb`,
                      opacity: 0.55,
                      backgroundColor: '#fafafa',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '180px',
                    }}
                    onClick={() => onSelectTable(table.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') onSelectTable(table.id); }}
                    aria-label={`${table.tableName} (filtered out)`}
                  >
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED }}>{table.tableName}</div>
                    <div style={{ fontSize: '0.65rem', color: TEXT_MUTED, marginTop: '2px' }}>T{table.tableNumber}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Confirmation Dialog Overlay */}
      {confirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setConfirmDialog(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 700, color: TEXT_DARK }}>
              Confirm Seating Assignment
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: TEXT_DARK }}>
              Seat <strong>{confirmDialog.order.referenceId}</strong> ({confirmDialog.order.guestCount} guest{confirmDialog.order.guestCount !== 1 ? 's' : ''}) at <strong>{confirmDialog.table.tableName}</strong>?
            </p>
            {confirmDialog.warnings.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '10px 12px', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ffcc80' }}>
                {confirmDialog.warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: '#e65100', marginBottom: i < confirmDialog.warnings.length - 1 ? '4px' : 0 }}>
                    ⚠️ {w}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDialog(null)}
                style={{
                  padding: '8px 18px',
                  borderRadius: '6px',
                  border: `1px solid ${BORDER_LIGHT}`,
                  background: '#f5f5f5',
                  color: TEXT_DARK,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSeat}
                style={{
                  padding: '8px 18px',
                  borderRadius: '6px',
                  border: 'none',
                  background: PRIMARY,
                  color: '#fff',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Order Pill with Popover ─────────────────────────────────────────────────

function OrderPillWithPopover({ order, locked, tierColor, pillBg, setDragOrder, setDropTargetTableId }: {
  order: OrderGroup;
  locked: boolean;
  tierColor: string;
  pillBg: string;
  setDragOrder: (o: OrderGroup | null) => void;
  setDropTargetTableId: (id: string | null) => void;
}) {
  const [showPopover, setShowPopover] = useState(false);

  // Check if any guest has ticket number >= 810 (late addition indicator)
  const hasHighTicket = order.guests.some((g) => {
    if (!g.ticketNumber) return false;
    const num = parseInt(g.ticketNumber.replace(/\D/g, ''), 10);
    return !isNaN(num) && num >= 810;
  });

  const effectivePillBg = hasHighTicket ? '#fce4ec' : pillBg;
  const effectiveBorder = hasHighTicket ? '#f48fb1' : (locked ? '#bbb' : tierColor);
  const effectiveColor = hasHighTicket ? '#c2185b' : (locked ? '#999' : tierColor);

  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShowPopover(true)}
      onMouseLeave={() => setShowPopover(false)}
    >
      <span
        draggable={!locked}
        onDragStart={(e) => {
          if (locked) { e.preventDefault(); return; }
          e.dataTransfer.setData('application/order-pill', JSON.stringify(order));
          setDragOrder(order);
          setShowPopover(false);
        }}
        onDragEnd={() => { setDragOrder(null); setDropTargetTableId(null); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 10px',
          borderRadius: '14px',
          backgroundColor: effectivePillBg,
          border: `1px solid ${effectiveBorder}`,
          color: effectiveColor,
          fontSize: '0.72rem',
          fontWeight: 600,
          cursor: locked ? 'not-allowed' : 'grab',
          opacity: locked ? 0.5 : 1,
          userSelect: 'none',
        }}
        title={locked ? 'Locked: family/RES group' : `Drag to seat ${order.referenceId} (${order.guestCount} guests)${hasHighTicket ? ' • Late addition (TKT ≥ 810)' : ''}`}
      >
        {locked && <span>🔒</span>}
        <span>{order.referenceId}</span>
        <span style={{ fontSize: '0.62rem', color: locked ? '#aaa' : TEXT_MUTED }}>({order.guestCount})</span>
      </span>

      {showPopover && order.guests.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 150,
          marginBottom: '6px',
          background: '#fff',
          border: `1px solid ${BORDER_LIGHT}`,
          borderRadius: '8px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          padding: '8px 10px',
          minWidth: '220px',
          whiteSpace: 'nowrap',
        }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '4px', borderBottom: `1px solid ${BORDER_LIGHT}`, paddingBottom: '3px' }}>
            {order.referenceId} — {order.guestCount} guest{order.guestCount !== 1 ? 's' : ''}
          </div>
          {order.guests.map((g, i) => {
            const tktNum = g.ticketNumber ? parseInt(g.ticketNumber.replace(/\D/g, ''), 10) : 0;
            const isHigh = tktNum >= 810;
            return (
              <div key={g.id} style={{ fontSize: '0.72rem', padding: '2px 0', color: TEXT_DARK, borderBottom: i < order.guests.length - 1 ? '1px solid #f5f5f5' : 'none', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span>{g.fullName}</span>
                <span style={{ fontSize: '0.6rem', color: isHigh ? '#c2185b' : '#1565c0', fontFamily: 'monospace', fontWeight: isHigh ? 700 : 400 }}>
                  {g.ticketNumber || '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}

// ─── Unseated Popover ────────────────────────────────────────────────────────

function UnseatedPopover({ count, guests }: { count: number; guests: UnseatedGuestInfo[] }) {
  const [show, setShow] = useState(false);

  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span style={{ fontSize: '0.78rem', color: '#e65100', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
        {count} unseated
      </span>
      {show && guests.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 100,
          marginTop: '4px',
          background: '#fff',
          border: `1px solid ${BORDER_LIGHT}`,
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          padding: '10px 12px',
          minWidth: '380px',
          maxHeight: '320px',
          overflowY: 'auto',
        }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '6px', borderBottom: `1px solid ${BORDER_LIGHT}`, paddingBottom: '4px' }}>
            Unseated Guests ({count})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '3px 5px', color: TEXT_MUTED, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase' }}>#</th>
                <th style={{ textAlign: 'left', padding: '3px 5px', color: TEXT_MUTED, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '3px 5px', color: TEXT_MUTED, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase' }}>Ticket</th>
                <th style={{ textAlign: 'left', padding: '3px 5px', color: TEXT_MUTED, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase' }}>Order</th>
                <th style={{ textAlign: 'left', padding: '3px 5px', color: TEXT_MUTED, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase' }}>Family</th>
              </tr>
            </thead>
            <tbody>
              {guests.sort((a, b) => (a.orderReferenceId || 'zzz').localeCompare(b.orderReferenceId || 'zzz')).map((g, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '2px 5px', color: TEXT_MUTED, borderBottom: '1px solid #f5f5f5' }}>{idx + 1}</td>
                  <td style={{ padding: '2px 5px', color: TEXT_DARK, fontWeight: 500, borderBottom: '1px solid #f5f5f5' }}>{g.fullName}</td>
                  <td style={{ padding: '2px 5px', color: TEXT_MUTED, fontFamily: 'monospace', fontSize: '0.65rem', borderBottom: '1px solid #f5f5f5' }}>{g.ticketNumber || '—'}</td>
                  <td style={{ padding: '2px 5px', color: '#1565c0', fontFamily: 'monospace', fontSize: '0.65rem', borderBottom: '1px solid #f5f5f5' }}>{g.orderReferenceId || '—'}</td>
                  <td style={{ padding: '2px 5px', color: TEXT_MUTED, fontSize: '0.65rem', borderBottom: '1px solid #f5f5f5' }}>{g.familyAssociation || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </span>
  );
}

// ─── Circular Table Card ─────────────────────────────────────────────────────

function CircularTableCard({ table, onClick, onDragStart, dragOrder, dropTargetTableId, onOrderDragOver, onOrderDragLeave, onOrderDrop }: { table: TableInfo; onClick: () => void; onDragStart?: () => void; dragOrder: OrderGroup | null; dropTargetTableId: string | null; onOrderDragOver: (tableId: string) => void; onOrderDragLeave: () => void; onOrderDrop: (order: OrderGroup, table: TableInfo) => void }) {
  const [showGuests, setShowGuests] = useState(false);
  const occupied = table.seats.filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn').length;
  const available = table.seats.filter((s) => s.status === 'Available').length;
  const totalSeats = table.seats.length;
  const pct = totalSeats > 0 ? Math.round((occupied / totalSeats) * 100) : 0;
  const seatedGuests = table.seats.filter((s) => s.guestName).sort((a, b) => (a.orderReferenceId || 'zzz').localeCompare(b.orderReferenceId || 'zzz'));

  // VIP circle shading
  const isVipZone = table.zoneName.toLowerCase().includes('vip');
  const hasVipGuest = table.seats.some((s) => s.protocolVip);
  const circleFill = isVipZone ? '#e0f7fa' : hasVipGuest ? '#fff9c4' : '#f5f7f6';

  // Arrange seats in a circle
  const radius = 52;
  const centerX = 70;
  const centerY = 70;
  const seatRadius = 8;

  // Determine if this table is a valid drop target for the current drag order
  const isValidDropTarget = dragOrder !== null && available >= dragOrder.guestCount;
  const isActiveDropTarget = dropTargetTableId === table.id;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      draggable={table.isEmpty}
      onDragStart={(e) => {
        if (!table.isEmpty) { e.preventDefault(); return; }
        e.dataTransfer.setData('text/plain', table.id);
        onDragStart?.();
      }}
      onDragEnd={() => {}}
      onDragOver={(e) => {
        if (!dragOrder) return;
        e.preventDefault();
        onOrderDragOver(table.id);
      }}
      onDragLeave={() => {
        if (!dragOrder) return;
        onOrderDragLeave();
      }}
      onDrop={(e) => {
        if (!dragOrder) return;
        e.preventDefault();
        e.stopPropagation();
        onOrderDrop(dragOrder, table);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      aria-label={`${table.tableName}: ${occupied} occupied, ${available} available${table.isEmpty ? ' (draggable)' : ''}`}
      style={{
        backgroundColor: isActiveDropTarget ? '#e8f5e9' : BG_CARD,
        border: isActiveDropTarget
          ? '2px dashed #4caf50'
          : dragOrder && isValidDropTarget
            ? `2px dashed #81c784`
            : `1px solid ${BORDER_LIGHT}`,
        borderRadius: '12px',
        padding: '12px',
        cursor: table.isEmpty ? 'grab' : 'pointer',
        transition: 'box-shadow 0.2s, border-color 0.2s, background-color 0.2s',
        position: 'relative',
        opacity: table.isEmpty ? 1 : 1,
      }}
      onMouseEnter={(e) => {
        if (!dragOrder) {
          (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(26,107,60,0.15)';
          (e.currentTarget as HTMLElement).style.borderColor = PRIMARY;
        }
        setShowGuests(true);
      }}
      onMouseLeave={(e) => {
        if (!dragOrder) {
          (e.currentTarget as HTMLElement).style.boxShadow = 'none';
          (e.currentTarget as HTMLElement).style.borderColor = BORDER_LIGHT;
        }
        setShowGuests(false);
      }}
    >
      {/* Table header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: TEXT_DARK }}>{table.tableName}</div>
        <div style={{ fontSize: '0.65rem', color: TEXT_MUTED }}>T{table.tableNumber}</div>
      </div>

      {/* Circular seat visualization */}
      <svg width="140" height="140" viewBox="0 0 140 140" style={{ display: 'block', margin: '0 auto' }}>
        {/* Table circle */}
        <circle cx={centerX} cy={centerY} r={34} fill={circleFill} stroke={BORDER_LIGHT} strokeWidth="2" />
        {/* Occupancy ring */}
        <circle
          cx={centerX}
          cy={centerY}
          r={34}
          fill="none"
          stroke={pct === 100 ? '#7b1fa2' : pct > 75 ? '#f9a825' : PRIMARY}
          strokeWidth="3"
          strokeDasharray={`${(pct / 100) * 213.6} 213.6`}
          strokeLinecap="round"
          transform={`rotate(-90 ${centerX} ${centerY})`}
          opacity={0.8}
        />
        {/* Center text */}
        <text x={centerX} y={centerY - 6} textAnchor="middle" fontSize="14" fontWeight="800" fill={TEXT_DARK}>{occupied}/{totalSeats}</text>
        <text x={centerX} y={centerY + 10} textAnchor="middle" fontSize="9" fill={TEXT_MUTED}>{pct}% full</text>

        {/* Seat dots arranged in a circle */}
        {table.seats.sort((a, b) => a.seatNumber - b.seatNumber).map((seat, idx) => {
          const angle = (idx / totalSeats) * 2 * Math.PI - Math.PI / 2;
          const sx = centerX + radius * Math.cos(angle);
          const sy = centerY + radius * Math.sin(angle);
          const color = SEAT_COLORS[seat.status] || '#999';
          const isVipSeat = seat.protocolVip && !isVipZone;
          return (
            <g key={seat.id}>
              <circle cx={sx} cy={sy} r={seatRadius} fill={color} opacity={0.85} stroke={isVipSeat ? '#f57c00' : 'none'} strokeWidth={isVipSeat ? 2.5 : 0} />
              <text x={sx} y={sy + 3} textAnchor="middle" fontSize="7" fill="#fff" fontWeight="600">{seat.seatNumber}</text>
              {isVipSeat && (
                <text x={sx} y={sy - seatRadius - 2} textAnchor="middle" fontSize="8" fontWeight="900" fill="#f57c00">▾</text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Footer pills */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
        <span style={{ padding: '6px 14px', borderRadius: '20px', backgroundColor: '#e8f5e9', color: '#2e7d32', textAlign: 'center', lineHeight: 1.2 }}>
          <span style={{ display: 'block', fontSize: '0.6rem', fontWeight: 400 }}>Avail</span>
          <span style={{ display: 'block', fontSize: '1.1rem', fontWeight: 800 }}>{available}</span>
        </span>
        <span style={{ padding: '6px 14px', borderRadius: '20px', backgroundColor: '#e3f2fd', color: '#1565c0', textAlign: 'center', lineHeight: 1.2 }}>
          <span style={{ display: 'block', fontSize: '0.6rem', fontWeight: 400 }}>Filled</span>
          <span style={{ display: 'block', fontSize: '1.1rem', fontWeight: 800 }}>{occupied}</span>
        </span>
      </div>

      {/* Guest popover on hover */}
      {showGuests && seatedGuests.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '100%',
          zIndex: 200,
          marginLeft: '8px',
          background: '#fff',
          border: `1px solid ${BORDER_LIGHT}`,
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          padding: '10px 12px',
          minWidth: '420px',
          maxHeight: '280px',
          overflowY: 'auto',
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '6px', borderBottom: `1px solid ${BORDER_LIGHT}`, paddingBottom: '4px' }}>
            {table.tableName} — {seatedGuests.length} guest{seatedGuests.length !== 1 ? 's' : ''}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '2px 4px', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600 }}>Seat</th>
                <th style={{ textAlign: 'left', padding: '2px 4px', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600 }}>Guest</th>
                <th style={{ textAlign: 'left', padding: '2px 4px', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600 }}>Ticket</th>
                <th style={{ textAlign: 'left', padding: '2px 4px', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600 }}>Chapter</th>
                <th style={{ textAlign: 'left', padding: '2px 4px', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600 }}>Family</th>
                <th style={{ textAlign: 'left', padding: '2px 4px', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600 }}>Order</th>
              </tr>
            </thead>
            <tbody>
              {seatedGuests.map((s) => (
                <tr key={s.id} style={s.protocolVip && !isVipZone ? { backgroundColor: '#fff9c4' } : undefined}>
                  <td style={{ padding: '2px 4px', color: TEXT_MUTED, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>{s.seatNumber}</td>
                  <td style={{ padding: '2px 4px', color: TEXT_DARK, fontWeight: 500, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>
                    {s.protocolVip && !isVipZone && <span style={{ fontWeight: 900, color: '#f57c00', marginRight: '3px' }}>&gt;&gt;</span>}
                    {s.guestName}
                  </td>
                  <td style={{ padding: '2px 4px', color: '#1565c0', fontFamily: 'monospace', fontSize: '0.62rem', borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>{s.ticketNumber || '—'}</td>
                  <td style={{ padding: '2px 4px', color: '#0d9488', fontSize: '0.62rem', borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>{s.chapterAffiliation || '—'}</td>
                  <td style={{ padding: '2px 4px', color: '#7c3aed', fontSize: '0.62rem', borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>{s.familyAssociation || '—'}</td>
                  <td style={{ padding: '2px 4px', color: '#7b1fa2', fontFamily: 'monospace', fontSize: '0.62rem', borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>{s.orderReferenceId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
